import React, { useState, useEffect } from 'react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { GuidedMethodologyStep, StepCondition } from "../../lib/firebase";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";
import { X, Plus, Trash2 } from "lucide-react";

// Dialog components
const Dialog = DialogPrimitive.Root;
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-3xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg rounded-lg max-h-[90vh] overflow-y-auto",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);

// Create textarea component
const Textarea = React.forwardRef<
  HTMLTextAreaElement, 
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => {
  return (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
Textarea.displayName = "Textarea";

// Select component
const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, ...props }, ref) => {
  return (
    <select
      className={cn(
        "flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
Select.displayName = "Select";

interface StepEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (step: GuidedMethodologyStep) => void;
  step: GuidedMethodologyStep;
  availableSteps: GuidedMethodologyStep[];
}

export const StepEditDialog: React.FC<StepEditDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  step,
  availableSteps
}) => {
  const [editedStep, setEditedStep] = useState<GuidedMethodologyStep>({...step});
  const [newTool, setNewTool] = useState("");
  const [newToolUrl, setNewToolUrl] = useState("");
  const [newCommand, setNewCommand] = useState("");
  const [newCondition, setNewCondition] = useState<StepCondition>({
    id: `condition-${Date.now()}`,
    condition: "equals",
    value: "",
    nextStepId: "end"
  });

  // Reset form when step changes
  useEffect(() => {
    setEditedStep({...step});
  }, [step]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Ensure no undefined values
    const sanitizedStep: GuidedMethodologyStep = {
      ...editedStep,
      id: editedStep.id || `step-${Date.now()}`,
      title: editedStep.title || '',
      description: editedStep.description || '',
      toolSuggestions: editedStep.toolSuggestions || [],
      toolSuggestionUrls: editedStep.toolSuggestionUrls || {},
      commandExamples: editedStep.commandExamples || [],
      guidanceNotes: editedStep.guidanceNotes || '',
      requiresUserInput: Boolean(editedStep.requiresUserInput),
      inputFieldLabel: editedStep.requiresUserInput ? (editedStep.inputFieldLabel || 'Enter input') : '',
      inputDataType: editedStep.requiresUserInput ? (editedStep.inputDataType || 'Text') : 'Text',
      nextStepType: editedStep.nextStepType || 'Linear',
      
      // For Linear flow, handle nextStepId ensuring 'end' is set to null/undefined consistently
      // For Conditional flow, ensure conditions array exists and has no undefined values
      ...(editedStep.nextStepType === 'Linear' 
        ? { 
            // When "end" is selected, set to undefined (not null)
            // This is for consistency when saving to Firestore
            nextStepId: editedStep.nextStepId === 'end' ? undefined : editedStep.nextStepId,
            conditions: [] // Empty array for Linear flow
          }
        : { 
            nextStepId: undefined, // Not used in Conditional flow
            conditions: (editedStep.conditions || []).map(c => ({
              id: c.id || `condition-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
              condition: c.condition || 'equals',
              value: c.value || '',
              // Keep 'end' as a string for consistency
              nextStepId: c.nextStepId || 'end'
            }))
          }
      )
    };
    
    onSave(sanitizedStep);
  };

  // Text field update handler
  const handleInputChange = (field: keyof GuidedMethodologyStep, value: string) => {
    setEditedStep(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Boolean field update handler
  const handleCheckboxChange = (field: keyof GuidedMethodologyStep, checked: boolean) => {
    setEditedStep(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  // Next step type handler
  const handleNextStepTypeChange = (value: 'Linear' | 'Conditional') => {
    setEditedStep(prev => ({
      ...prev,
      nextStepType: value,
      // Initialize conditions array if switching to conditional
      conditions: value === 'Conditional' && (!prev.conditions || prev.conditions.length === 0) 
        ? [{ 
            id: `condition-${Date.now()}`, 
            condition: "equals", 
            value: "", 
            nextStepId: "end" 
          }] 
        : prev.conditions
    }));
  };

  // Add a new tool
  const handleAddTool = () => {
    if (!newTool.trim()) return;

    setEditedStep(prev => {
      const updatedTools = [...(prev.toolSuggestions || []), newTool.trim()];
      
      // Add URL if provided
      let updatedUrls = {...(prev.toolSuggestionUrls || {})};
      if (newToolUrl.trim()) {
        updatedUrls[newTool.trim()] = newToolUrl.trim();
      }
      
      return {
        ...prev,
        toolSuggestions: updatedTools,
        toolSuggestionUrls: updatedUrls
      };
    });
    
    setNewTool("");
    setNewToolUrl("");
  };

  // Remove a tool
  const handleRemoveTool = (index: number) => {
    setEditedStep(prev => {
      const updatedTools = [...(prev.toolSuggestions || [])];
      const removedTool = updatedTools[index];
      updatedTools.splice(index, 1);
      
      // Remove URL if it exists
      const updatedUrls = {...(prev.toolSuggestionUrls || {})};
      if (removedTool && updatedUrls[removedTool]) {
        delete updatedUrls[removedTool];
      }
      
      return {
        ...prev,
        toolSuggestions: updatedTools,
        toolSuggestionUrls: updatedUrls
      };
    });
  };

  // Add a new command example
  const handleAddCommand = () => {
    if (!newCommand.trim()) return;
    
    setEditedStep(prev => ({
      ...prev,
      commandExamples: [...(prev.commandExamples || []), newCommand.trim()]
    }));
    
    setNewCommand("");
  };

  // Remove a command example
  const handleRemoveCommand = (index: number) => {
    setEditedStep(prev => {
      const updatedCommands = [...(prev.commandExamples || [])];
      updatedCommands.splice(index, 1);
      
      return {
        ...prev,
        commandExamples: updatedCommands
      };
    });
  };

  // Add a new condition
  const handleAddCondition = () => {
    if (!newCondition.value.trim()) return;
    
    setEditedStep(prev => ({
      ...prev,
      conditions: [...(prev.conditions || []), newCondition]
    }));
    
    setNewCondition({
      id: `condition-${Date.now()}`,
      condition: "equals",
      value: "",
      nextStepId: "end"
    });
  };

  // Remove a condition
  const handleRemoveCondition = (index: number) => {
    setEditedStep(prev => {
      const updatedConditions = [...(prev.conditions || [])];
      updatedConditions.splice(index, 1);
      
      return {
        ...prev,
        conditions: updatedConditions
      };
    });
  };

  // Update a condition field
  const handleConditionChange = (index: number, field: keyof StepCondition, value: string) => {
    setEditedStep(prev => {
      const updatedConditions = [...(prev.conditions || [])];
      updatedConditions[index] = {
        ...updatedConditions[index],
        [field]: value
      };
      
      return {
        ...prev,
        conditions: updatedConditions
      };
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {step.id.startsWith('step-') && !availableSteps.some(s => s.id === step.id) 
              ? 'Add New Step' 
              : 'Edit Step'
            }
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Info Section */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="title">Step Title</Label>
              <Input 
                id="title"
                value={editedStep.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter step title"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={editedStep.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter description"
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="guidanceNotes">Guidance Notes</Label>
              <Textarea
                id="guidanceNotes"
                value={editedStep.guidanceNotes}
                onChange={(e) => handleInputChange('guidanceNotes', e.target.value)}
                placeholder="Provide detailed guidance for this step"
                rows={3}
              />
            </div>
          </div>
          
          {/* Tools Section */}
          <div className="border p-4 rounded-md">
            <h3 className="font-medium mb-3">Tool Suggestions</h3>
            
            {/* Existing Tools */}
            {editedStep.toolSuggestions && editedStep.toolSuggestions.length > 0 && (
              <div className="mb-4 space-y-2">
                {editedStep.toolSuggestions.map((tool, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-1 p-2 bg-gray-50 rounded-md text-sm">
                      <strong>{tool}</strong>
                      {editedStep.toolSuggestionUrls && editedStep.toolSuggestionUrls[tool] && (
                        <div className="text-xs mt-1 text-gray-500">
                          URL: {editedStep.toolSuggestionUrls[tool]}
                        </div>
                      )}
                    </div>
                    <Button 
                      type="button" 
                      onClick={() => handleRemoveTool(index)}
                      className="h-8 w-8 p-0 flex items-center justify-center text-red-500 bg-transparent hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New Tool */}
            <div className="grid grid-cols-1 gap-2">
              <div className="flex gap-2">
                <Input
                  value={newTool}
                  onChange={(e) => setNewTool(e.target.value)}
                  placeholder="Tool name (e.g., Nmap)"
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  onClick={handleAddTool}
                  className="px-3 bg-[#daed6e] hover:bg-[#c5d85c]"
                >
                  <Plus size={16} />
                </Button>
              </div>
              <Input
                value={newToolUrl}
                onChange={(e) => setNewToolUrl(e.target.value)}
                placeholder="Tool URL (optional)"
              />
            </div>
          </div>
          
          {/* Command Examples Section */}
          <div className="border p-4 rounded-md">
            <h3 className="font-medium mb-3">Command Examples</h3>
            
            {/* Existing Commands */}
            {editedStep.commandExamples && editedStep.commandExamples.length > 0 && (
              <div className="mb-4 space-y-2">
                {editedStep.commandExamples.map((command, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-gray-50 rounded-md text-sm font-mono">
                      {command}
                    </code>
                    <Button 
                      type="button" 
                      onClick={() => handleRemoveCommand(index)}
                      className="h-8 w-8 p-0 flex items-center justify-center text-red-500 bg-transparent hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New Command */}
            <div className="flex gap-2">
              <Input
                value={newCommand}
                onChange={(e) => setNewCommand(e.target.value)}
                placeholder="Command example (e.g., nmap -sV target.com)"
                className="flex-1"
              />
              <Button 
                type="button" 
                onClick={handleAddCommand}
                className="px-3 bg-[#daed6e] hover:bg-[#c5d85c]"
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>
          
          {/* User Input Section */}
          <div className="border p-4 rounded-md">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="requiresUserInput"
                checked={editedStep.requiresUserInput || false}
                onChange={(e) => handleCheckboxChange('requiresUserInput', e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="requiresUserInput" className="m-0">Requires User Input</Label>
            </div>
            
            {editedStep.requiresUserInput && (
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label htmlFor="inputFieldLabel">Input Field Label</Label>
                  <Input
                    id="inputFieldLabel"
                    value={editedStep.inputFieldLabel || ''}
                    onChange={(e) => handleInputChange('inputFieldLabel', e.target.value)}
                    placeholder="Enter input field label"
                  />
                </div>
                
                <div>
                  <Label htmlFor="inputDataType">Input Data Type</Label>
                  <Select
                    id="inputDataType"
                    value={editedStep.inputDataType || 'Text'}
                    onChange={(e) => handleInputChange('inputDataType', e.target.value as any)}
                  >
                    <option value="Text">Text</option>
                    <option value="Number">Number</option>
                    <option value="List">List</option>
                  </Select>
                </div>
              </div>
            )}
          </div>
          
          {/* Next Step Logic Section */}
          <div className="border p-4 rounded-md">
            <h3 className="font-medium mb-3">Next Step Logic</h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="nextStepType">Flow Type</Label>
                <Select
                  id="nextStepType"
                  value={editedStep.nextStepType}
                  onChange={(e) => handleNextStepTypeChange(e.target.value as 'Linear' | 'Conditional')}
                >
                  <option value="Linear">Linear (Single Path)</option>
                  <option value="Conditional">Conditional (Multiple Paths)</option>
                </Select>
              </div>
              
              {/* Linear Path */}
              {editedStep.nextStepType === 'Linear' && (
                <div>
                  <Label htmlFor="nextStepId">Next Step</Label>
                  <Select
                    id="nextStepId"
                    value={editedStep.nextStepId || 'end'}
                    onChange={(e) => handleInputChange('nextStepId', e.target.value === 'end' ? undefined : e.target.value)}
                  >
                    <option value="end">End (No Next Step)</option>
                    {availableSteps
                      .filter(s => s.id !== editedStep.id) // Don't allow self-reference
                      .map(s => (
                        <option key={s.id} value={s.id}>
                          {s.title}
                        </option>
                      ))
                    }
                  </Select>
                </div>
              )}
              
              {/* Conditional Path */}
              {editedStep.nextStepType === 'Conditional' && (
                <div className="space-y-4">
                  {/* Existing Conditions */}
                  {editedStep.conditions && editedStep.conditions.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium">Conditions:</h4>
                      {editedStep.conditions.map((condition, index) => (
                        <div key={condition.id} className="flex gap-2 items-center bg-gray-50 p-3 rounded-md">
                          <div className="grid grid-cols-3 gap-2 flex-1">
                            <Select
                              value={condition.condition}
                              onChange={(e) => handleConditionChange(index, 'condition', e.target.value)}
                            >
                              <option value="equals">Equals</option>
                              <option value="contains">Contains</option>
                              <option value="startsWith">Starts With</option>
                              <option value="endsWith">Ends With</option>
                            </Select>
                            
                            <Input
                              value={condition.value}
                              onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                              placeholder="Match value"
                            />
                            
                            <Select
                              value={condition.nextStepId}
                              onChange={(e) => handleConditionChange(index, 'nextStepId', e.target.value)}
                            >
                              <option value="end">End (No Next Step)</option>
                              {availableSteps
                                .filter(s => s.id !== editedStep.id) // Don't allow self-reference
                                .map(s => (
                                  <option key={s.id} value={s.id}>
                                    {s.title}
                                  </option>
                                ))
                              }
                            </Select>
                          </div>
                          
                          <Button 
                            type="button" 
                            onClick={() => handleRemoveCondition(index)}
                            className="h-8 w-8 p-0 flex items-center justify-center text-red-500 bg-transparent hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add New Condition */}
                  <div className="pt-2">
                    <h4 className="text-sm font-medium mb-2">Add New Condition:</h4>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="grid grid-cols-3 gap-2">
                        <Select
                          value={newCondition.condition}
                          onChange={(e) => setNewCondition({...newCondition, condition: e.target.value})}
                        >
                          <option value="equals">Equals</option>
                          <option value="contains">Contains</option>
                          <option value="startsWith">Starts With</option>
                          <option value="endsWith">Ends With</option>
                        </Select>
                        
                        <Input
                          value={newCondition.value}
                          onChange={(e) => setNewCondition({...newCondition, value: e.target.value})}
                          placeholder="Match value"
                        />
                        
                        <Select
                          value={newCondition.nextStepId}
                          onChange={(e) => setNewCondition({...newCondition, nextStepId: e.target.value})}
                        >
                          <option value="end">End (No Next Step)</option>
                          {availableSteps
                            .filter(s => s.id !== editedStep.id) // Don't allow self-reference
                            .map(s => (
                              <option key={s.id} value={s.id}>
                                {s.title}
                              </option>
                            ))
                          }
                        </Select>
                      </div>
                      
                      <Button 
                        type="button" 
                        onClick={handleAddCondition}
                        className="bg-[#daed6e] hover:bg-[#c5d85c] w-full"
                      >
                        Add Condition
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-100 text-gray-800 hover:bg-gray-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#daed6e] hover:bg-[#c5d85c]"
            >
              Save Step
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 